import { Box } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import type { JSX } from "react";

import { REDUCED, breathe, ping, sweep } from "./motion.js";

/**
 * The decorative ornaments for the `/tester` under-construction hero, extracted
 * from `TesterPage.tsx`: the "Coming soon" header badge, the lime live dot, the
 * radar signal emitter, and the indeterminate progress sweep. All are
 * presentational; the emitter's whole stage is `aria-hidden`.
 */

/** Small navy "Coming soon" badge for the header's right slot. */
export function ComingSoonBadge(): JSX.Element {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        borderRadius: 999,
        border: 1,
        borderColor: (t: Theme) => alpha(t.palette.primary.main, 0.35),
        bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.1),
        px: 1.25,
        py: 0.5,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.04em",
        color: "primary.main",
      }}
    >
      <Box
        aria-hidden
        component="span"
        sx={{ height: 6, width: 6, borderRadius: "50%", bgcolor: "primary.main" }}
      />
      Coming soon
    </Box>
  );
}

/** The lime live-status dot — the one energetic accent on the page. */
export function LiveDot(): JSX.Element {
  return (
    <Box
      aria-hidden
      component="span"
      sx={{
        height: 8,
        width: 8,
        flexShrink: 0,
        borderRadius: "50%",
        bgcolor: "secondary.main",
        boxShadow: (t: Theme) => `0 0 0 3px ${alpha(t.palette.secondary.main, 0.18)}`,
        animation: `${breathe} 2.4s ease-in-out infinite`,
        [REDUCED]: { animation: "none" },
      }}
    />
  );
}

/**
 * The focal visual: a diagnostics node emitting concentric test signals. Static
 * guide rings keep the radar read even when motion is reduced; the animated
 * ping rings layer on top for the live emit. Entirely decorative, so the whole
 * stage is `aria-hidden`.
 */
export function SignalEmitter(): JSX.Element {
  const NODE = 72;
  const STAGE = 260;
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        justifySelf: "center",
        display: "grid",
        placeItems: "center",
        height: STAGE,
        width: STAGE,
        maxWidth: "100%",
      }}
    >
      {/* Soft brand glow bloom behind everything. */}
      <Box
        sx={{
          position: "absolute",
          height: "78%",
          width: "78%",
          borderRadius: "50%",
          bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.1),
          filter: "blur(46px)",
        }}
      />

      {/* Static guide rings — the always-on radar scaffold. */}
      {[1, 0.66, 0.4].map((scale) => (
        <Box
          key={`guide-${scale}`}
          sx={{
            position: "absolute",
            height: `${STAGE * scale}px`,
            width: `${STAGE * scale}px`,
            borderRadius: "50%",
            border: 1,
            borderColor: (t: Theme) => alpha(t.palette.primary.main, 0.12),
          }}
        />
      ))}

      {/* Animated ping rings — staggered so the emit looks continuous. */}
      {[0, 1, 2].map((i) => (
        <Box
          key={`ping-${i}`}
          sx={{
            position: "absolute",
            height: STAGE,
            width: STAGE,
            borderRadius: "50%",
            border: 1,
            borderColor: (t: Theme) => alpha(t.palette.primary.main, 0.5),
            animation: `${ping} 3s cubic-bezier(0.16,1,0.3,1) infinite`,
            animationDelay: `${i * 1}s`,
            [REDUCED]: { animation: "none", opacity: 0 },
          }}
        />
      ))}

      {/* The node itself — brand gradient disc with the diagnostics glyph. */}
      <Box
        sx={{
          position: "relative",
          display: "grid",
          placeItems: "center",
          height: NODE,
          width: NODE,
          borderRadius: "50%",
          background: (t: Theme) =>
            `linear-gradient(140deg, ${t.palette.primary.light} 0%, ${t.palette.primary.main} 100%)`,
          color: "primary.contrastText",
          boxShadow: (t: Theme) =>
            `0 8px 24px -8px ${alpha(t.palette.primary.main, 0.7)}, inset 0 1px 0 ${alpha(t.palette.common.white, 0.25)}`,
        }}
      >
        <TroubleshootIcon sx={{ fontSize: 34 }} />
      </Box>
    </Box>
  );
}

/** Indeterminate progress: a bright navy segment sweeps the track. Under
 * reduced motion it settles to a static partial fill so it still reads as
 * "work in progress" rather than empty. */
export function ProgressSweep(): JSX.Element {
  return (
    <Box
      role="progressbar"
      aria-label="Tester surface under construction"
      aria-valuetext="In progress"
      sx={{
        position: "relative",
        height: 6,
        width: "100%",
        overflow: "hidden",
        borderRadius: 999,
        bgcolor: "surfaceSoft",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          width: "42%",
          borderRadius: 999,
          background: (t: Theme) =>
            `linear-gradient(90deg, ${alpha(t.palette.primary.main, 0)} 0%, ${t.palette.primary.light} 50%, ${alpha(t.palette.primary.main, 0)} 100%)`,
          animation: `${sweep} 1.9s cubic-bezier(0.45,0,0.55,1) infinite`,
          [REDUCED]: {
            animation: "none",
            width: "56%",
            background: (t: Theme) => t.palette.primary.main,
          },
        }}
      />
    </Box>
  );
}
